import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/app/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, DownloadSimple, PencilSimple, Trash, CalendarBlank, Users, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";

const LINK_TYPE_LABEL = {
  project: "Project", donor: "Donor report", appointment: "Appointment",
  compliance: "Compliance", procurement: "Procurement", client: "Client",
  department: "Department", internal: "Internal",
};

const ACTION_STATUS = ["open", "in_progress", "done", "overdue"];

export default function MeetingMinutes() {
  const { user } = useAuth();
  const canEdit = hasMinRole(user, "staff");
  const canDelete = hasMinRole(user, "manager");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/meetings");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing?.id) await api.patch(`/meetings/${editing.id}`, payload);
      else await api.post("/meetings", payload);
      toast.success("Meeting saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete minutes "${m.title}"?`)) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (m) => {
    try {
      const res = await api.get(`/meetings/${m.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `minutes-${m.meeting_date}-${m.title.replace(/\s+/g, "-").slice(0, 40)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = filter === "all" ? items : items.filter((m) => m.link_type === filter);

  return (
    <div data-testid="meetings-page">
      <PageHeader
        eyebrow="Records"
        title="Meeting Minutes"
        description="Central record of every meeting — agendas, decisions, action items and links to projects, donors, appointments or compliance."
        actions={canEdit && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary-blue" data-testid="new-meeting-btn">
            <Plus size={14} className="mr-1" /> New minutes
          </Button>
        )}
      />
      <div className="space-y-5 p-5 md:p-8">
        <div className="dc-card p-3 flex flex-wrap items-center gap-3">
          <Tag size={14} className="text-slate-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-48" data-testid="meeting-link-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All meetings</SelectItem>
              {Object.entries(LINK_TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{filtered.length} shown</span>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={CalendarBlank}
            title="No minutes yet"
            description={canEdit ? "Click 'New minutes' to record your first meeting." : "No meetings recorded for this filter."}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filtered.map((m) => (
              <article key={m.id} className="dc-card overflow-hidden" data-testid={`meeting-card-${m.id}`}>
                <header className="border-b border-slate-100 bg-slate-50/60 px-5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-base font-semibold text-slate-900 truncate">{m.title}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {formatDate(m.meeting_date)}
                        {m.time ? ` · ${m.time}` : ""}
                        {m.location ? ` · ${m.location}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {m.link_type && m.link_type !== "internal" && (
                        <span className="font-mono-alt rounded bg-blue-100 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-blue-700">
                          {LINK_TYPE_LABEL[m.link_type] || m.link_type}
                        </span>
                      )}
                      {m.department && <span className="text-[10px] text-slate-500">{m.department}</span>}
                    </div>
                  </div>
                  {m.link_label && <div className="mt-1 text-[11px] font-medium text-slate-700">↳ {m.link_label}</div>}
                </header>
                <div className="space-y-3 p-5 text-sm">
                  {m.participants?.length > 0 && (
                    <div>
                      <div className="overline flex items-center gap-1"><Users size={10} /> Participants · {m.participants.length}</div>
                      <div className="mt-1 text-xs text-slate-700">{m.participants.join(", ")}</div>
                    </div>
                  )}
                  {m.agenda && (
                    <div>
                      <div className="overline">Agenda</div>
                      <div className="mt-1 whitespace-pre-wrap text-xs text-slate-700">{m.agenda}</div>
                    </div>
                  )}
                  {m.discussion && (
                    <div>
                      <div className="overline">Discussion</div>
                      <div className="mt-1 whitespace-pre-wrap text-xs text-slate-700 line-clamp-4">{m.discussion}</div>
                    </div>
                  )}
                  {m.action_items?.length > 0 && (
                    <div>
                      <div className="overline">Action items · {m.action_items.length}</div>
                      <ul className="mt-1 space-y-1">
                        {m.action_items.slice(0, 5).map((a, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            <StatusBadge status={a.status === "done" ? "approved" : a.status === "overdue" ? "expired" : a.status === "in_progress" ? "pending" : "pending_review"} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-slate-900">{a.description}</div>
                              <div className="text-[10px] text-slate-500">
                                {a.responsible || "unassigned"}{a.target_date ? ` · due ${a.target_date}` : ""}
                              </div>
                            </div>
                          </li>
                        ))}
                        {m.action_items.length > 5 && <li className="text-[10px] text-slate-500">+{m.action_items.length - 5} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
                <footer className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-2.5">
                  <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                    by {m.created_by} · {m.created_at?.slice(0, 10)}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => download(m)} data-testid={`meeting-download-${m.id}`}>
                      <DownloadSimple size={12} className="mr-1" /> PDF
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => { setEditing(m); setOpen(true); }} data-testid={`meeting-edit-${m.id}`}>
                        <PencilSimple size={12} />
                      </Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="outline" onClick={() => remove(m)} data-testid={`meeting-delete-${m.id}`}>
                        <Trash size={12} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>

      <MeetingDialog open={open} onClose={() => { setOpen(false); setEditing(null); }} editing={editing} onSave={save} />
    </div>
  );
}

function MeetingDialog({ open, onClose, editing, onSave }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (open) {
      setForm(editing ? { ...editing } : {
        title: "", meeting_date: new Date().toISOString().slice(0, 10),
        time: "", location: "", participants: [], agenda: "",
        discussion: "", decisions: "",
        action_items: [], link_type: "internal", link_id: "",
        link_label: "", department: "",
      });
    }
  }, [open, editing]);
  if (!form) return null;

  const addAction = () => setForm({
    ...form,
    action_items: [...(form.action_items || []), { description: "", responsible: "", target_date: "", status: "open" }],
  });
  const updateAction = (i, patch) => setForm({
    ...form,
    action_items: form.action_items.map((a, j) => j === i ? { ...a, ...patch } : a),
  });
  const removeAction = (i) => setForm({
    ...form,
    action_items: form.action_items.filter((_, j) => j !== i),
  });

  const submit = () => {
    if (!form.title || !form.meeting_date) return toast.error("Title and date required");
    onSave({
      ...form,
      participants: Array.isArray(form.participants) ? form.participants
        : (form.participants || "").split(",").map((s) => s.trim()).filter(Boolean),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit" : "New"} Meeting Minutes</DialogTitle>
          <DialogDescription>Record the meeting details. Fields can be edited later. Minutes are downloadable as a formatted PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-field-title" /></div>
            <div><Label>Date *</Label><Input type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} /></div>
            <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          </div>
          <div><Label>Participants (comma-separated)</Label>
            <Input
              value={Array.isArray(form.participants) ? form.participants.join(", ") : form.participants}
              onChange={(e) => setForm({ ...form, participants: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Jane Doe, John Smith, …"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div><Label>Link type</Label>
              <Select value={form.link_type} onValueChange={(v) => setForm({ ...form, link_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LINK_TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Link ID (optional)</Label><Input value={form.link_id} onChange={(e) => setForm({ ...form, link_id: e.target.value })} placeholder="e.g. project/donor id" /></div>
            <div><Label>Link label</Label><Input value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} placeholder="Human-friendly reference" /></div>
          </div>
          <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Finance / Projects / HR / …" /></div>
          <div><Label>Agenda</Label><Textarea rows={3} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} /></div>
          <div><Label>Discussion summary</Label><Textarea rows={4} value={form.discussion} onChange={(e) => setForm({ ...form, discussion: e.target.value })} /></div>
          <div><Label>Decisions taken</Label><Textarea rows={2} value={form.decisions} onChange={(e) => setForm({ ...form, decisions: e.target.value })} /></div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Action items</Label>
              <Button size="sm" variant="outline" onClick={addAction} data-testid="meeting-add-action"><Plus size={10} className="mr-1" /> Add</Button>
            </div>
            <ul className="mt-2 space-y-2">
              {(form.action_items || []).map((a, i) => (
                <li key={i} className="grid grid-cols-1 gap-2 rounded-md border border-slate-100 bg-slate-50/50 p-2 md:grid-cols-[1fr_140px_130px_110px_30px]">
                  <Input placeholder="Action *" value={a.description} onChange={(e) => updateAction(i, { description: e.target.value })} />
                  <Input placeholder="Responsible" value={a.responsible || ""} onChange={(e) => updateAction(i, { responsible: e.target.value })} />
                  <Input type="date" value={a.target_date || ""} onChange={(e) => updateAction(i, { target_date: e.target.value })} />
                  <Select value={a.status || "open"} onValueChange={(v) => updateAction(i, { status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTION_STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => removeAction(i)}><Trash size={10} /></Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="btn-primary-blue" data-testid="meeting-save">Save minutes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
